// Shared micro-components for the Nutrition Progress content views.
// KcalChip — adherence-tinted panel for kcal values (Calendar cells, avg rows).
// MiniBar — 3px horizontal fill bar for per-macro progress (Calendar cells).
// Dot — bigger-than-body-text " · " separator between joined text segments
// (CalendarHeader day count, plan-timeline kcal/meal-plan summary parts).
// ChartLegend — swatch-row legend for Recharts Legend content render prop —
// every series (line or bar) gets the same solid-square swatch, no attempt
// at a dashed-line icon for dashed series; used by Hero and Reports charts.

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { ADH_COLOR, translateCalendarGrid, tdeeWarningState } from "./viewTokens";
import { useMeasuredTooltipRef } from "../../../../../hooks/useCenteredTooltip";

export const Dot = () => <span className="np-cal-dot"> · </span>;

export const MiniBar = ({ value, target, color, label, unit }) => (
  <div className="np-mini-bar">
    <span className="np-mini-bar__label" style={{ color }}>
      {label}
    </span>
    <div className="np-mini-bar__track">
      <div
        className="np-mini-bar__fill"
        style={{
          background: color,
          width: `${Math.min(((value || 0) / Math.max(target || 1, 1)) * 100, 100)}%`,
        }}
      />
    </div>
    <span className="np-mini-bar__value" style={{ color }}>
      {Math.round(value || 0)}
      {unit}
    </span>
  </div>
);

MiniBar.propTypes = {
  value: PropTypes.number,
  target: PropTypes.number,
  color: PropTypes.string,
  label: PropTypes.string,
  unit: PropTypes.string,
};

export const KcalChip = ({ kcal, token, sub, large = false }) => {
  const { t } = useTranslation("athlete-portal");
  // `token` is one of ADH_COLOR's fixed keys (good/warn/bad/none) — a small
  // enum, so its color is a CSS modifier class (np-kcal-chip--TOKEN in
  // nutrition.css), never an inline style. ADH_COLOR itself stays the
  // source of truth for which tokens are valid; only its color VALUES are
  // duplicated into CSS (kept in sync manually, same tradeoff as elsewhere).
  const tokenClass = ADH_COLOR[token] ? token : "none";
  const chipClass = ["np-kcal-chip", `np-kcal-chip--${tokenClass}`, large && "np-kcal-chip--large"]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={chipClass}>
      <span className="np-kcal-chip__value">
        {kcal == null
          ? "—"
          : `${Math.round(kcal).toLocaleString()} ${translateCalendarGrid(t, "kcalUnit")}`}
      </span>
      {sub && <span className="np-kcal-chip__sub">({sub})</span>}
    </div>
  );
};

KcalChip.propTypes = {
  kcal: PropTypes.number,
  token: PropTypes.string,
  sub: PropTypes.string,
  large: PropTypes.bool,
};

// `coloredText` is opt-in (only ReportsMacroChart.js) — tints the label
// text to match its own swatch instead of the shared muted --white-70.
// `entry.textColor` overrides just the label color when coloredText is on,
// independent of `entry.color` (which drives the swatch/dash) — e.g. Target
// dash/line is --primary (theme-aware, matches the chart stroke) while its
// label text stays --white-50, so the two intentionally don't match here.
// Falls back to `entry.color` when omitted (every other entry today).
// `entry.swatchShape` of "dashedLine" renders a short dashed line instead of
// the default solid square — for series drawn as a dashed Line (e.g. Target),
// where a solid square would misrepresent how the series actually looks on
// the chart. This is OUR OWN field, not Recharts' auto-derived entry.type
// (which is "line" for every Line regardless of dash pattern, including
// Hero/intake-chart solid Intake/Bodyweight lines) — keeping it separate
// means only chart payloads that explicitly opt in are affected.
export const ChartLegend = ({ payload, coloredText }) => (
  <div className="np-hero__chart-legend">
    {payload.map((entry) => (
      <span
        key={entry.dataKey}
        className="np-hero__chart-legend-item"
        style={coloredText ? { color: entry.textColor ?? entry.color } : undefined}
      >
        {entry.swatchShape === "dashedLine" ? (
          <span className="np-hero__chart-legend-dash" style={{ borderColor: entry.color }} />
        ) : (
          <span className="np-hero__chart-legend-swatch" style={{ background: entry.color }} />
        )}
        {entry.value}
      </span>
    ))}
  </div>
);

ChartLegend.propTypes = { payload: PropTypes.array, coloredText: PropTypes.bool };

// Custom label for a TDEE ReferenceLine (SummaryHero.js / ReportsIntakeChart.js)
// — a real component (rather than Recharts' plain label config) so it can
// carry hover state for the decoupled-warning tooltip. Recharts injects
// viewBox/value automatically when label is a custom element or component
// (confirmed in ReferenceLine.js — Label.renderCallByParent passes the
// line's own rect as viewBox to whatever label is given).
// warningText is pre-interpolated (not translated in here) — this stays a
// dumb presentational component, same convention as ChartLegend above.
//
// Positioned INSIDE the plot area, right-aligned just before its right edge
// and sitting on top of the line — NOT in the right margin past that edge
// (which is where Recharts' own default label config, and this component
// originally, put it). That margin is the weight Y-axis's own space: the
// label rendered directly on top of its tick values ("80" and "TDEE" drawn
// over each other). Shifting the label up/down can't fix that — the axis
// has ticks along its full height, so every vertical position collides with
// one. Anchoring end-of-text inside the plot keeps it clear of the axis
// entirely while staying attached to the end of the line it describes.
const LABEL_EDGE_GAP = 8; // from the plot's right edge
const LABEL_LINE_GAP = 5; // above the reference line itself
// Warning tooltip box, and how far it clears the label/line it hangs off.
const TOOLTIP_HEIGHT = 72;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_GAP = 6;

export const TdeeReferenceLabel = ({ viewBox, value, decoupled, warningText, color, warnColor }) => {
  const [hovered, setHovered] = useState(false);
  if (!viewBox) return null;
  const x = viewBox.x + viewBox.width - LABEL_EDGE_GAP;
  const lineY = viewBox.y + viewBox.height / 2;
  const y = lineY - LABEL_LINE_GAP;

  // The tooltip normally hangs ABOVE the label, but a high TDEE line leaves
  // no room there and the SVG clips it (y < 0 is outside the viewport). Flip
  // it below the line in that case. viewBox.y is the line's real position in
  // SVG coordinates, so this is a direct check, not a guess.
  const tooltipAbove = y - TOOLTIP_HEIGHT - TOOLTIP_GAP >= 0;
  const tooltipY = tooltipAbove ? y - TOOLTIP_HEIGHT - TOOLTIP_GAP : lineY + TOOLTIP_GAP;

  // Plain, non-interactive label — the common case (decoupled=false).
  if (!decoupled) {
    return (
      <text x={x} y={y} fontSize={10} fill={color} textAnchor="end" dominantBaseline="auto">
        {value}
      </text>
    );
  }

  // Decoupled branch — genuinely interactive (role="button" as a static
  // literal — biome's a11y check needs it unconditional to recognize the
  // element as interactive; a ternary in the prop isn't evaluated) with
  // real keyboard support (focus + Enter/Space), not just a mouse hover.
  const toggleHover = (e) => {
    if (e.key === "Enter" || e.key === " ") setHovered((prev) => !prev);
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: SVG group, not swappable for a real button element
    <g
      role="button"
      tabIndex={0}
      aria-label={warningText}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={toggleHover}
      style={{ cursor: "pointer" }}
    >
      <text x={x} y={y} fontSize={10} fill={warnColor} textAnchor="end" dominantBaseline="auto">
        ⚠ {value}
      </text>
      {/* Right edge aligned with the (end-anchored) label's own, so the
          tooltip stays inside the plot instead of spilling over the axis. */}
      {hovered && (
        <foreignObject
          x={x - TOOLTIP_WIDTH}
          y={tooltipY}
          width={TOOLTIP_WIDTH}
          height={TOOLTIP_HEIGHT}
          style={{ overflow: "visible" }}
        >
          <div className="np-hero__tooltip np-hero__tooltip--decoupled">{warningText}</div>
        </foreignObject>
      )}
    </g>
  );
};

TdeeReferenceLabel.propTypes = {
  viewBox: PropTypes.object,
  value: PropTypes.node,
  decoupled: PropTypes.bool,
  warningText: PropTypes.string,
  color: PropTypes.string,
  warnColor: PropTypes.string,
};

// TDEE reference-line colors — same tokens HeroChart.js's TdeeCard
// (SummaryHero.js) already uses for this exact "decoupled" state.
const TDEE_COLOR = "var(--gradient-right)";
const DECOUPLED_COLOR = "var(--energy)";

// Shared TDEE <ReferenceLine> props builder — HeroChart.js
// (SummaryHeroChart.js), ReportsIntakeChart.js and ReportsMacroChart.js all
// draw the exact same dashed horizontal TDEE line (recolored + with a hover
// warning when tdeeWarningState says the weight-change back-calc is
// "decoupled" — see that function, viewTokens.js). Returns null when
// there's nothing to draw yet.
//
// This is a props-builder, not a <TdeeReferenceLine> wrapper component,
// deliberately: Recharts' ComposedChart finds ReferenceLine children by
// their literal element type (findAllByType) when deciding what to draw —
// a wrapper component's rendered output has a different `type`, so Recharts
// silently never draws it. Confirmed by rendering a wrapped vs. a
// spread-props ReferenceLine to static markup and diffing the SVG — only
// the spread-props version drew the line. Callers keep
// `<ReferenceLine {...tdeeReferenceLineProps(...)} />` as a literal element.
export const tdeeReferenceLineProps = ({ workingExpenditure, decoupled, staleData, weeklyChange, yAxisId, t }) => {
  if (workingExpenditure == null) return null;
  const { isWarn, warningText } = tdeeWarningState({ staleData, decoupled, weeklyChange }, t);
  return {
    yAxisId,
    y: workingExpenditure,
    stroke: isWarn ? DECOUPLED_COLOR : TDEE_COLOR,
    strokeDasharray: "4 2",
    label: (
      <TdeeReferenceLabel
        value={t("nutritionPage.progress.hero.chart.tdee")}
        decoupled={isWarn}
        warningText={warningText}
        color={TDEE_COLOR}
        warnColor={DECOUPLED_COLOR}
      />
    ),
  };
};

// Shared wrapper for every chart's custom Recharts Tooltip content render
// prop — extracted once a 5th near-identical tooltip appeared (Hero
// ChartTooltip, ReportsIntakeChart IntakeTooltip, ReportsMacroChart
// MacroTooltip, ReportsWeekdayChart WeekdayTooltip, BodyweightGoal
// GoalTooltip). Each only differs in what it renders for the header/rows,
// not in how it gets there (measure width, guard active/payload, the
// .np-hero__tooltip surface). `children` is a render-prop (pt, payload) =>
// node for the chart-specific content; `isEmpty(pt, payload)` is an optional
// extra guard for when a point technically exists but has nothing worth
// showing (e.g. WeekdayChart pt?.pctOffTarget == null) — returning true
// renders nothing, not even the empty shell.
export const TooltipShell = ({ active, payload, onMeasureWidth, isEmpty, children }) => {
  const boxRef = useMeasuredTooltipRef(onMeasureWidth);
  if (!active || !payload?.length) return null;
  const pt = payload[0]?.payload;
  if (isEmpty?.(pt, payload)) return null;
  return (
    <div className="np-hero__tooltip" ref={boxRef}>
      {children(pt, payload)}
    </div>
  );
};

TooltipShell.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  onMeasureWidth: PropTypes.func,
  isEmpty: PropTypes.func,
  children: PropTypes.func.isRequired,
};
